import HandleRequest, { ApiMeta } from "./HandleRequest";

/** The columns the customer list shows. The API returns the whole row. */
export type Customer = {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  cell_phone: string | null;
  house_phone: string | null;
  status: string | null;
  date_came_in: string | null;
};

/**
 * Which row actions the caller's group may use. Decided by
 * CustomerAccess::permissions() on the server, never in the browser.
 */
export type CustomerPermissions = {
  can_invoice: boolean;
  can_create_order: boolean;
  can_edit: boolean;
  can_delete: boolean;
};

export type CustomersMeta = ApiMeta & { permissions?: CustomerPermissions };

/** CustomersController::ALLOWED_SORT_FIELDS, minus the ones no column uses. */
export const CUSTOMER_SORTS = [
  "user_id",
  "last_name",
  "first_name",
  "date_came_in",
  "cell_phone",
  "house_phone",
  "status",
] as const;

export type CustomerSort = (typeof CUSTOMER_SORTS)[number];

export type CustomersQuery = {
  page?: number;
  perPage?: number;
  sort?: CustomerSort;
  order?: "asc" | "desc";
  search?: string;
};

export const NO_PERMISSIONS: CustomerPermissions = {
  can_invoice: false,
  can_create_order: false,
  can_edit: false,
  can_delete: false,
};

const { getRequest } = HandleRequest();

function buildQuery(query: CustomersQuery): string {
  const params = new URLSearchParams();

  if (query.page) params.set("page", String(query.page));
  if (query.perPage) params.set("per_page", String(query.perPage));
  if (query.sort) params.set("sort", query.sort);
  if (query.order) params.set("order", query.order);

  const search = query.search?.trim();
  if (search) params.set("search", search);

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

export default function CustomersService() {
  return {
    list: async (query: CustomersQuery = {}) =>
      getRequest(`/api/v1/customers${buildQuery(query)}`),
  };
}
